import { Loader } from '../Widgets';
import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { toastr } from 'react-redux-toastr'
import moment from 'moment';
import { useSelector } from 'react-redux';
import { RootState } from 'store/store';
import { Employee } from 'service/Models';
import ServerGateway from 'service/ServerGateway';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCircleInfo, faDownload, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import AsyncSelect from 'react-select/async';
import { components } from 'react-select';
import Swal from 'sweetalert2';

export const Regularization = () => {
    const user = useSelector((state: RootState) => state.appdata);
    const [month, setMonth] = useState<string>(moment().format("YYYY-MM"));
    const [employeeId, setEmployeeId] = useState<number>(0);
    const [regularizations, setRegularizations] = useState<any[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        loadRegularizations();
    }, [month, employeeId]);

    const loadRegularizations = () => {
        setIsLoading(true);
        const from = moment(month).startOf('month').format("YYYY-MM-DD");
        const to = moment(month).endOf('month').format("YYYY-MM-DD");
        ServerGateway
            .getRegularizations(employeeId, "-1", from, to, 0, 0)
            .then((data) => {
                setRegularizations(data.entries);
                setIsLoading(false);
            })
            .catch(error => {
                console.log(error);
                setIsLoading(false);
            });
    }

    const handleMonthChange = (e) => {
        setMonth(e.target.value);
    }

    const getClasNameByStatus = (status) => {
        if (status == 2) {
            return "badge bg-success";
        }
        else if (status == 3) {
            return "badge bg-danger";
        }
        else {
            return "badge bg-primary";
        }
    }

    const getApprovalStatusName = (status) => {
        console.log(status);
        if (status == 1) {
            return "Pending";
        }
        else if (status == 2) {
            return "Approved";
        }
        else if (status == 3) {
            return "Rejected";
        }
    }

    const handleEmployeeSelection = (selectedValue) => {
        if (selectedValue) {
            setEmployeeId(selectedValue.value);
        }
        else {
            setEmployeeId(0);
        }
    }

    const handleEmployeeSearch = (inputValue: string) => {
        return ServerGateway.getEmployees(inputValue, 0, 1, 10)
            .then((data) => {
                return data.entries.map(function (employee) {
                    return {
                        value: employee.id,
                        label: (
                            <span
                                dangerouslySetInnerHTML={{
                                    __html: `<b>${employee.name}</b><br /><i>${employee.employeeNumber}</i> - ${employee.groupName} - ${employee.designationName}`,
                                }}
                            />
                        ),
                    };
                });
            });
    }

    const approveRegularization = async (requestId) => {
        // Step 1: Show confirmation dialog
        const confirmResult = await Swal.fire({
            title: 'Approve Regularization Request?',
            text: 'Are you sure you want to approve this regularization request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, approve it!',
            cancelButtonText: 'Cancel'
        });

        if (!confirmResult.isConfirmed) {
            return; // User cancelled
        }

        // Step 2: Show comment input dialog
        const commentResult = await Swal.fire({
            title: 'Add Comment (Optional)',
            text: 'You can add a comment for this approval:',
            input: 'textarea',
            inputPlaceholder: 'Enter your comment here...',
            inputAttributes: {
                'aria-label': 'Type your comment here',
                'maxlength': '500' // Changed from number to string
            },
            showCancelButton: true,
            confirmButtonText: 'Approve',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
                if (value && value.length > 500) {
                    return 'Comment cannot exceed 500 characters';
                }
                return undefined; // Explicitly return undefined for valid input
            }
        });

        if (!commentResult.isConfirmed) {
            return; // User cancelled at comment stage
        }

        const comment = commentResult.value || ''; // Get comment or empty string

        // Step 3: Show loading state
        Swal.fire({
            title: 'Processing...',
            text: 'Approving regularization request',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        ServerGateway.approveRegularization(requestId, 2, comment || '')
            .then(() => {
                // Step 4: Close loading state
                Swal.close();

                // Step 5: Show success message
                toastr.success("Success", "Regularization request approved successfully.");

                // Step 6: Reload data
                loadRegularizations();
            })
            .catch(error => {
                console.error('Error approving regularization:', error);

                // Show error message
                toastr.error("Error", "Failed to approve regularization request.");

                // Close loading state
                Swal.close();
            });
    }

    const cancelRegularization = async (requestId) => {
        // Step 1: Show confirmation dialog
        const confirmResult = await Swal.fire({
            title: 'Reject Regularization Request?',
            text: 'Are you sure you want to reject this regularization request?',
            icon: 'question',
            showCancelButton: true,
            confirmButtonColor: '#3085d6',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Yes, reject it!',
            cancelButtonText: 'Cancel'
        });

        if (!confirmResult.isConfirmed) {
            return; // User cancelled
        }

        // Step 2: Show comment input dialog
        const commentResult = await Swal.fire({
            title: 'Add Comment (Optional)',
            text: 'You can add a comment for this rejection:',
            input: 'textarea',
            inputPlaceholder: 'Enter your comment here...',
            inputAttributes: {
                'aria-label': 'Type your comment here',
                'maxlength': '500' // Changed from number to string
            },
            showCancelButton: true,
            confirmButtonText: 'Reject',
            cancelButtonText: 'Cancel',
            inputValidator: (value) => {
                if (value && value.length > 500) {
                    return 'Comment cannot exceed 500 characters';
                }
                return undefined; // Explicitly return undefined for valid input
            }
        });

        if (!commentResult.isConfirmed) {
            return; // User cancelled at comment stage
        }

        const comment = commentResult.value || ''; // Get comment or empty string

        // Step 3: Show loading state
        Swal.fire({
            title: 'Processing...',
            text: 'Rejecting regularization request',
            allowOutsideClick: false,
            allowEscapeKey: false,
            showConfirmButton: false,
            didOpen: () => {
                Swal.showLoading();
            }
        });

        ServerGateway.approveRegularization(requestId, 3, comment || '')
            .then(() => {
                // Step 4: Close loading state
                Swal.close();

                // Step 5: Show success message
                toastr.success("Success", "Regularization request rejected successfully.");

                // Step 6: Reload data
                loadRegularizations();
            })
            .catch(error => {
                console.error('Error approving regularization:', error);

                // Show error message
                toastr.error("Error", "Failed to reject regularization request.");

                // Close loading state
                Swal.close();
            });
    }

    return (
        <div className="page-regularization">
            <div className='container'>
                <div className='row'>
                    <h2>Regularizations</h2>
                </div>
                <div className='row mb-2'>
                    <input type="month" className="form-control" value={month} onChange={handleMonthChange} style={{ textAlign: "center" }}></input>
                </div>
                <div className='row mb-2'>
                    <AsyncSelect
                        cacheOptions
                        defaultOptions
                        isClearable
                        onChange={handleEmployeeSelection}
                        loadOptions={handleEmployeeSearch}
                        placeholder="Select an employee"
                        className="form-control p-0"
                        style={{ padding: 0 }}
                    />
                </div>
                <div className="row">
                    <div className="btn bg-success" onClick={loadRegularizations}>
                        Reload Data
                    </div>
                </div>
                <div className='row' style={{ marginTop: "20px" }}>
                    {isLoading &&
                        <Loader />
                    }
                    {!isLoading &&
                        <>
                            {(regularizations.length == 0) &&
                                <div className="alert alert-secondary" role="alert">
                                    <FontAwesomeIcon icon={faCircleInfo} style={{ marginRight: 10 }} />
                                    No regularization request available
                                </div>
                            }
                            {(regularizations.length > 0) &&
                                <ul className="list-group list-group-flush">
                                    {regularizations && regularizations.map((request) => (
                                        <li className="list-group-item card" style={{ marginBottom: "20px" }}>
                                            <ul className="list-group list-group-flush">
                                                <li className="list-group-item">
                                                    <div className="fw-bold" style={{ fontSize: "1.2rem" }}>
                                                        {request.employeeName}
                                                    </div>
                                                    <span>{request.oldStatus} {'->'} {request.newStatus}</span>
                                                </li>
                                                <li className="list-group-item">
                                                    <span>On <b>{moment(request.fromDate).format("DD/MM/YYYY, dddd")}</b></span>
                                                </li>
                                                <li className="list-group-item">
                                                    Reason
                                                    <div className="fw-bold">
                                                        {request.reason}
                                                    </div>
                                                </li>
                                                <li className="list-group-item">
                                                    Remarks
                                                    <div className="fw-bold">
                                                        {request.remarks}
                                                    </div>
                                                </li>
                                                {request.approvalStatus > 1 &&
                                                    <li className="list-group-item">
                                                        Status
                                                        <div className="fw-bold">
                                                            <div className={getClasNameByStatus(request.approvalStatus)}>{getApprovalStatusName(request.approvalStatus)}</div>
                                                        </div>
                                                    </li>
                                                }
                                                {request.approvalStatus == 1 &&
                                                    <li className="list-group-item">
                                                        <div className="btn btn-success float-end" onClick={() => approveRegularization(request.id)}>Approve</div>
                                                        <div className="btn btn-sm btn-danger" onClick={() => cancelRegularization(request.id)}>Cancel</div>
                                                    </li>
                                                }
                                            </ul>
                                        </li>
                                    ))}
                                </ul>
                            }
                        </>
                    }
                </div>
            </div>
        </div>
    );
}